import {
  DocumentData,
  QueryConstraint,
  count,
  doc,
  getAggregateFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  sum,
  where
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, logDevelopmentError, stringArrayField, stringField } from "./firestoreHelpers";
import { mapBookingRecord, mapPaymentRecord, mapShoutOutRecord, BookingRecord, PaymentRecord } from "./realtimeMappers";
import { ShoutOutRecord } from "./types";

export type CoachAdminFilters = {
  activeOnly: boolean;
  withServices: boolean;
  withEarnings: boolean;
  noBookings: boolean;
  startDate: string;
  endDate: string;
};

export type CoachAdminProfile = {
  id: string;
  displayName: string | null;
  email: string | null;
  username: string | null;
  role: string | null;
  roles: string[];
  verificationStatus: string | null;
  verificationType: string | null;
  isSuspended: boolean;
  servicesOffered: string[];
  feedbackEnabled: boolean;
  feedbackPriceUSD: number | null;
  feedbackSupportedModes: string[];
  createdAt: Date | null;
  updatedAt: Date | null;
};

export type CoachEarningsSummary = {
  grossRevenue: number | null;
  chorFee: number | null;
  estimatedCoachEarnings: number | null;
  sourceRecordCount: number | null;
};

export type CoachAdminRow = CoachAdminProfile &
  CoachEarningsSummary & {
    statusLabel: string;
    totalBookings: number | null;
    payoutStatus: string | null;
  };

export type CoachPayoutRecord = {
  id: string;
  status: string | null;
  amountUSD: number | null;
  createdAt: Date | null;
  updatedAt: Date | null;
};

export type CoachEarningRecord = {
  id: string;
  sourceTitle: string;
  status: string | null;
  grossRevenue: number | null;
  chorFee: number | null;
  estimatedCoachEarnings: number | null;
  createdAt: Date | null;
  availableAt: Date | null;
};

export type CoachEarningAdjustmentRecord = {
  id: string;
  earningId: string | null;
  bookingId: string | null;
  marketplaceTransactionId: string | null;
  refundId: string | null;
  adjustmentAmountUSD: number | null;
  remainingAmountUSD: number | null;
  status: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
};

export type CoachAdminData = {
  coaches: CoachAdminRow[];
  lastUpdated: Date;
};

export type CoachDetailData = {
  coach: CoachAdminRow | null;
  bookings: BookingRecord[];
  earnings: CoachEarningRecord[];
  earningAdjustments: CoachEarningAdjustmentRecord[];
  payouts: CoachPayoutRecord[];
  shoutOuts: ShoutOutRecord[];
  paymentIssues: PaymentRecord[];
  unavailableSections: string[];
  lastUpdated: Date;
};

const COACH_QUERY_LIMIT = 60;
const DETAIL_LIMIT = 40;

export const initialCoachFilters: CoachAdminFilters = {
  activeOnly: false,
  withServices: false,
  withEarnings: false,
  noBookings: false,
  startDate: "",
  endDate: ""
};

function numberField(data: DocumentData, key: string): number | null {
  const value = data[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function currencyField(data: DocumentData, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = numberField(data, key);
    if (value !== null) {
      return value;
    }
  }

  return null;
}

function dateFromInput(value: string, endOfDay = false): Date | null {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return endOfDay ? new Date(year, month - 1, day + 1) : new Date(year, month - 1, day);
}

function dateConstraints(filters: CoachAdminFilters, fieldName = "createdAt"): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];
  const startDate = dateFromInput(filters.startDate);
  const endDate = dateFromInput(filters.endDate, true);

  if (startDate) {
    constraints.push(where(fieldName, ">=", startDate));
  }

  if (endDate) {
    constraints.push(where(fieldName, "<", endDate));
  }

  return constraints;
}

function serviceLabel(value: string) {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function servicesFromProfile(data: DocumentData): string[] {
  const services = new Set<string>();
  const supportedModes = stringArrayField(data, "feedbackSupportedModes");
  const feedbackPriceUSD = numberField(data, "feedbackPriceUSD");

  if (data.feedbackEnabled === true || supportedModes.length > 0 || feedbackPriceUSD !== null) {
    services.add("Coach feedback");
  }

  supportedModes.forEach((mode) => services.add(serviceLabel(mode)));

  if (feedbackPriceUSD !== null) {
    services.add(`Feedback $${feedbackPriceUSD.toFixed(2)}`);
  }

  return Array.from(services);
}

function mapCoachProfile(id: string, data: DocumentData): CoachAdminProfile {
  return {
    id,
    displayName: stringField(data, "displayName"),
    email: stringField(data, "email"),
    username: stringField(data, "username"),
    role: stringField(data, "role") ?? stringField(data, "activeRole"),
    roles: stringArrayField(data, "roles"),
    verificationStatus: stringField(data, "verificationStatus"),
    verificationType: stringField(data, "verificationType"),
    isSuspended: data.isSuspended === true,
    servicesOffered: servicesFromProfile(data),
    feedbackEnabled: data.feedbackEnabled === true,
    feedbackPriceUSD: numberField(data, "feedbackPriceUSD"),
    feedbackSupportedModes: stringArrayField(data, "feedbackSupportedModes"),
    createdAt: dateField(data, "createdAt"),
    updatedAt: dateField(data, "updatedAt")
  };
}

function statusLabel(coach: CoachAdminProfile): string {
  if (coach.isSuspended) {
    return "Inactive";
  }

  if (coach.verificationStatus === "approved" || coach.feedbackEnabled) {
    return "Active";
  }

  return "Inactive";
}

async function safeCoachQuery(constraints: QueryConstraint[]): Promise<CoachAdminProfile[]> {
  try {
    const snapshot = await getDocs(query(adminCollection(COLLECTIONS.users), ...constraints, limit(COACH_QUERY_LIMIT)));
    return snapshot.docs.map((document) => mapCoachProfile(document.id, document.data()));
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function fetchCoachProfiles(): Promise<CoachAdminProfile[]> {
  const results = await Promise.all([
    safeCoachQuery([where("verificationType", "==", "coach")]),
    safeCoachQuery([where("role", "==", "coach")]),
    safeCoachQuery([where("activeRole", "==", "coach")]),
    safeCoachQuery([where("roles", "array-contains", "coach")]),
    safeCoachQuery([where("feedbackEnabled", "==", true)])
  ]);
  const merged = new Map<string, CoachAdminProfile>();

  results.flat().forEach((coach) => merged.set(coach.id, coach));
  return Array.from(merged.values()).sort((left, right) => {
    const leftName = left.displayName ?? left.username ?? left.email ?? left.id;
    const rightName = right.displayName ?? right.username ?? right.email ?? right.id;
    return leftName.localeCompare(rightName);
  });
}

async function safeBookingCount(coachId: string, filters: CoachAdminFilters): Promise<number | null> {
  const constraints = dateConstraints(filters, "updatedAt");
  const coachIdFields = ["coachUserId", "providerUserId", "providerUserID"];
  let fallbackCount: number | null = null;

  for (const fieldName of coachIdFields) {
    try {
      const snapshot = await getAggregateFromServer(
        query(adminCollection(COLLECTIONS.bookings), where(fieldName, "==", coachId), ...constraints),
        { total: count() }
      );
      const total = snapshot.data().total;
      if (total > 0) {
        return total;
      }
      fallbackCount = total;
    } catch (error) {
      logDevelopmentError(error);
    }
  }

  return fallbackCount;
}

async function safeEarningsSummary(coachId: string, filters: CoachAdminFilters): Promise<CoachEarningsSummary> {
  try {
    /*
     * Firebase Functions writes backend-derived fee fields into coach_earnings.
     * TODO: keep this read-only UI sourced from a shared backend contract instead of duplicating marketplace fee math.
     */
    const snapshot = await getAggregateFromServer(
      query(adminCollection(COLLECTIONS.coachEarnings), where("coachUserId", "==", coachId), ...dateConstraints(filters)),
      {
        grossRevenue: sum("amount_usd"),
        chorFee: sum("platform_fee_usd"),
        estimatedCoachEarnings: sum("net_amount_usd"),
        sourceRecordCount: count()
      }
    );
    const data = snapshot.data();

    return {
      grossRevenue: data.grossRevenue ?? 0,
      chorFee: data.chorFee ?? 0,
      estimatedCoachEarnings: data.estimatedCoachEarnings ?? 0,
      sourceRecordCount: data.sourceRecordCount ?? 0
    };
  } catch (error) {
    logDevelopmentError(error);
    return {
      grossRevenue: null,
      chorFee: null,
      estimatedCoachEarnings: null,
      sourceRecordCount: null
    };
  }
}

async function safePayoutStatus(coachId: string): Promise<string | null> {
  const statuses: Array<{ status: string | null; date: Date | null }> = [];
  const queryVariants: QueryConstraint[][] = [
    [where("coachUserId", "==", coachId), orderBy("updatedAt", "desc"), limit(1)],
    [where("providerUserId", "==", coachId), orderBy("updatedAt", "desc"), limit(1)]
  ];

  for (const variant of queryVariants) {
    try {
      const snapshot = await getDocs(query(adminCollection(COLLECTIONS.coachPayouts), ...variant));
      snapshot.docs.forEach((document) => {
        const data = document.data();
        statuses.push({
          status: stringField(data, "status"),
          date: dateField(data, "updatedAt") ?? dateField(data, "createdAt")
        });
      });
    } catch (error) {
      logDevelopmentError(error);
    }
  }

  return statuses.sort((left, right) => (right.date?.getTime() ?? 0) - (left.date?.getTime() ?? 0))[0]?.status ?? null;
}

async function rowFromCoach(coach: CoachAdminProfile, filters: CoachAdminFilters): Promise<CoachAdminRow> {
  const [earnings, totalBookings, payoutStatus] = await Promise.all([
    safeEarningsSummary(coach.id, filters),
    safeBookingCount(coach.id, filters),
    safePayoutStatus(coach.id)
  ]);

  return {
    ...coach,
    ...earnings,
    statusLabel: statusLabel(coach),
    totalBookings,
    payoutStatus
  };
}

function matchesFilters(coach: CoachAdminRow, filters: CoachAdminFilters): boolean {
  if (filters.activeOnly && coach.statusLabel !== "Active") {
    return false;
  }

  if (filters.withServices && coach.servicesOffered.length === 0) {
    return false;
  }

  if (filters.withEarnings && (coach.estimatedCoachEarnings ?? 0) <= 0) {
    return false;
  }

  if (filters.noBookings && coach.totalBookings !== 0) {
    return false;
  }

  return true;
}

export async function fetchCoachesAdminData(filters: CoachAdminFilters = initialCoachFilters): Promise<CoachAdminData> {
  const profiles = await fetchCoachProfiles();
  const rows = await Promise.all(profiles.map((coach) => rowFromCoach(coach, filters)));

  return {
    coaches: rows.filter((coach) => matchesFilters(coach, filters)),
    lastUpdated: new Date()
  };
}

async function fetchCoachRow(coachId: string, filters: CoachAdminFilters): Promise<CoachAdminRow | null> {
  try {
    const snapshot = await getDoc(doc(db, COLLECTIONS.users, coachId));
    if (!snapshot.exists()) {
      return null;
    }

    return rowFromCoach(mapCoachProfile(snapshot.id, snapshot.data()), filters);
  } catch (error) {
    logDevelopmentError(error);
    return null;
  }
}

async function fetchBookingHistory(coachId: string): Promise<BookingRecord[]> {
  const docs = new Map<string, BookingRecord>();
  const queryVariants: QueryConstraint[][] = [
    [where("coachUserId", "==", coachId), orderBy("updatedAt", "desc"), limit(DETAIL_LIMIT)],
    [where("providerUserId", "==", coachId), orderBy("updatedAt", "desc"), limit(DETAIL_LIMIT)],
    [where("providerUserID", "==", coachId), orderBy("updatedAt", "desc"), limit(DETAIL_LIMIT)]
  ];

  for (const variant of queryVariants) {
    try {
      const snapshot = await getDocs(query(adminCollection(COLLECTIONS.bookings), ...variant));
      snapshot.docs.forEach((document) => docs.set(document.id, mapBookingRecord(document.id, document.data())));
    } catch (error) {
      logDevelopmentError(error);
    }
  }

  return Array.from(docs.values()).sort((left, right) => (right.updatedAt?.getTime() ?? 0) - (left.updatedAt?.getTime() ?? 0));
}

async function fetchEarningsBreakdown(coachId: string): Promise<CoachEarningRecord[]> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.coachEarnings), where("coachUserId", "==", coachId), orderBy("createdAt", "desc"), limit(DETAIL_LIMIT))
    );

    return snapshot.docs.map((document) => {
      const data = document.data();
      return {
        id: document.id,
        sourceTitle: stringField(data, "sourceTitle") ?? stringField(data, "sourceKind") ?? "Coach earning",
        status: stringField(data, "status"),
        grossRevenue: currencyField(data, "amount_usd", "grossAmountUSD"),
        chorFee: currencyField(data, "platform_fee_usd", "platformFeeUSD"),
        estimatedCoachEarnings: currencyField(data, "net_amount_usd", "providerPayoutUSD"),
        createdAt: dateField(data, "createdAt") ?? dateField(data, "completedAt"),
        availableAt: dateField(data, "availableAt")
      };
    });
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function fetchEarningAdjustments(coachId: string): Promise<CoachEarningAdjustmentRecord[]> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.coachEarningAdjustments), where("coachUserId", "==", coachId), orderBy("createdAt", "desc"), limit(DETAIL_LIMIT))
    );

    return snapshot.docs.map((document) => {
      const data = document.data();
      return {
        id: document.id,
        earningId: stringField(data, "earningId"),
        bookingId: stringField(data, "bookingId"),
        marketplaceTransactionId: stringField(data, "marketplaceTransactionId"),
        refundId: stringField(data, "refundId"),
        adjustmentAmountUSD: currencyField(data, "adjustmentAmountUSD"),
        remainingAmountUSD: currencyField(data, "reconcileAmountRemainingUSD"),
        status: stringField(data, "status"),
        createdAt: dateField(data, "createdAt"),
        updatedAt: dateField(data, "updatedAt")
      };
    });
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function fetchPayouts(coachId: string): Promise<CoachPayoutRecord[]> {
  const payouts = new Map<string, CoachPayoutRecord>();
  const queryVariants: QueryConstraint[][] = [
    [where("coachUserId", "==", coachId), orderBy("updatedAt", "desc"), limit(DETAIL_LIMIT)],
    [where("providerUserId", "==", coachId), orderBy("updatedAt", "desc"), limit(DETAIL_LIMIT)]
  ];

  for (const variant of queryVariants) {
    try {
      const snapshot = await getDocs(query(adminCollection(COLLECTIONS.coachPayouts), ...variant));
      snapshot.docs.forEach((document) => {
        const data = document.data();
        payouts.set(document.id, {
          id: document.id,
          status: stringField(data, "status"),
          amountUSD: currencyField(data, "amount_usd", "amountUSD", "net_amount_usd"),
          createdAt: dateField(data, "createdAt"),
          updatedAt: dateField(data, "updatedAt")
        });
      });
    } catch (error) {
      logDevelopmentError(error);
    }
  }

  return Array.from(payouts.values()).sort((left, right) => (right.updatedAt?.getTime() ?? 0) - (left.updatedAt?.getTime() ?? 0));
}

async function fetchCoachShoutOuts(coachId: string): Promise<ShoutOutRecord[]> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.peerShoutOuts), where("recipientDancerUserId", "==", coachId), orderBy("createdAt", "desc"), limit(DETAIL_LIMIT))
    );
    return snapshot.docs.map((document) => mapShoutOutRecord(document.id, document.data()));
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function fetchPaymentIssues(coachId: string): Promise<PaymentRecord[]> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.marketplaceTransactions), where("payeeUserId", "==", coachId), orderBy("updatedAt", "desc"), limit(DETAIL_LIMIT))
    );
    return snapshot.docs
      .map((document) => mapPaymentRecord(document.id, document.data()))
      .filter((payment) => {
        const status = payment.status?.toLowerCase() ?? "";
        return status.includes("fail") || status.includes("cancel") || status.includes("refund") || status.includes("dispute");
      });
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

export async function fetchCoachDetail(coachId: string): Promise<CoachDetailData> {
  const [coach, bookings, earnings, earningAdjustments, payouts, shoutOuts, paymentIssues] = await Promise.all([
    fetchCoachRow(coachId, initialCoachFilters),
    fetchBookingHistory(coachId),
    fetchEarningsBreakdown(coachId),
    fetchEarningAdjustments(coachId),
    fetchPayouts(coachId),
    fetchCoachShoutOuts(coachId),
    fetchPaymentIssues(coachId)
  ]);

  const unavailableSections: string[] = [];

  if (!coach?.sourceRecordCount) {
    unavailableSections.push("Earnings may be unavailable until coach_earnings documents are created by backend aggregation.");
  }

  if (!coach?.payoutStatus) {
    unavailableSections.push("Payout status unavailable when no coach_payouts record exists.");
  }

  return {
    coach,
    bookings,
    earnings,
    earningAdjustments,
    payouts,
    shoutOuts,
    paymentIssues,
    unavailableSections,
    lastUpdated: new Date()
  };
}
